/**
 * Membership "testing plan / cadence" content — ported (first pass) from the
 * standalone membership flow (ahead-membership → src/pages/MembershipPage.tsx)
 * and the marketing /membership page.
 *
 * Rendered as the content of the add-ons step (via AddOnsStep's `contentOverride`)
 * when the user picks "Become a member", so it reuses the existing step chrome —
 * the real PersistentDesktopSidebar (desktop) and ExpandableOrderSummary
 * (mobile). Content-only: the plan-selection section. Navigation, price total
 * and order summary come from the reused chrome.
 *
 * Prices come from the real membership tiers (Stripe source of truth, via
 * `@/utils/membershipPricing`); the strikethrough "booking separately" price is
 * derived from the baseline test + standalone follow-ups so the per-cadence
 * saving is visible.
 */
import { useEffect, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { BOOKING_NS, languageMap } from "@/i18n";
import { t as translate } from "@/locales";
import type { BookingLanguage } from "@/types/booking";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import {
  getCadencePriceChf,
  getSeparatePriceChf,
  FOLLOWUP_PRICE_CHF,
  FOLLOWUP_MEMBER_PRICE_CHF,
  type RecommendedPlan,
} from "@/utils/membershipPricing";
import { useMembershipTiers } from "@/hooks/useMembershipTiers";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { CheckCircle2, Circle } from "lucide-react";
import imgVialBase from "@/assets/membership/vial_base.svg";
import imgVialFollowup from "@/assets/membership/vial_followup.svg";

// ── Vial icons (Phosphor test-tubes) ───────────────────────────────────────────
function VialGroup({ followups }: { followups: number }) {
  return (
    <div className="flex items-center">
      <img src={imgVialBase} width={20} height={22} alt="" />
      {followups > 0 && (
        <>
          <span className="font-normal text-sm text-neutral-400 mx-1">+</span>
          <span className="flex items-center -space-x-1">
            {Array.from({ length: followups }).map((_, i) => (
              <img
                key={i}
                src={imgVialFollowup}
                width={20}
                height={22}
                alt=""
              />
            ))}
          </span>
        </>
      )}
    </div>
  );
}

// ── Plan card ─────────────────────────────────────────────────────────────────
interface PlanCardProps {
  language: BookingLanguage;
  selected: boolean;
  onSelect: () => void;
  label: string;
  description: string;
  price: number;
  /** Price if the same tests were booked separately (shown struck-through). */
  originalPrice?: number;
  followups: number;
  recommended?: boolean;
  /** Outlined "Baseline" badge — same as the Single blood test card on step 1. */
  baseline?: boolean;
}

function PlanCard({
  language,
  selected,
  onSelect,
  label,
  description,
  price,
  originalPrice,
  followups,
  recommended,
  baseline,
}: PlanCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-2xl p-4 normal-case transition-all bg-white shadow-card-sm border-2 ${
        selected
          ? "border-aubergine-600"
          : "border-transparent hover:shadow-card-hover"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Radio — circle / filled check (aligned with the title line) */}
        {selected ? (
          <CheckCircle2 size={24} className="shrink-0 text-aubergine-600" />
        ) : (
          <Circle size={24} className="shrink-0 text-neutral-300" />
        )}

        {/* Label + vials + description */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className="font-medium text-lg text-neutral-900">
              {label}
            </span>
            <VialGroup followups={followups} />
            {baseline && (
              <span className="inline-flex items-center rounded-full border border-neutral-200 px-2.5 py-0.5 text-xs font-medium tracking-[0.1px] text-neutral-900">
                {translate(language, "membershipCadence.baselineBadge")}
              </span>
            )}
            {recommended && (
              <span className="bg-deep-green-600 text-white font-medium text-xs px-2 py-0.5 rounded-full">
                {translate(language, "membershipCadence.recommendedBadge")}
              </span>
            )}
          </div>
          {/* Description — on desktop it sits under the title (left column).
              On mobile it moves to a full-width row below (see below) so it
              isn't squeezed into the narrow left column next to the price. */}
          <p className="hidden lg:block font-normal text-sm text-neutral-600">
            {description}
          </p>
        </div>

        {/* Price (+ struck-through "booking separately" price when it saves) */}
        <div className="flex flex-col items-end shrink-0 pl-3">
          {originalPrice && originalPrice > price && (
            <span className="font-normal text-xs text-neutral-400">
              <span className="line-through">CHF {originalPrice}</span>*
            </span>
          )}
          <div className="flex items-baseline gap-0.5">
            <span className="font-normal text-xs text-neutral-600">CHF</span>
            <span className="font-medium text-xl text-neutral-900">
              {price}
            </span>
            <span className="font-normal text-xs text-neutral-600">/year</span>
          </div>
        </div>
      </div>

      {/* Mobile-only full-width description — aligned under the title (clears the
          radio) and spans the row width instead of being cramped beside the price. */}
      <p className="lg:hidden mt-2 pl-9 font-normal text-sm text-neutral-600">
        {description}
      </p>
    </button>
  );
}

// ── Content ───────────────────────────────────────────────────────────────────
interface MembershipPlanStepProps {
  /** Seed the store with the recommended plan on mount. Disabled when the step
   *  is reused inside the "Change cadence" modal so it keeps the user's choice. */
  autoSeed?: boolean;
}

export const MembershipPlanStep = ({
  autoSeed = true,
}: MembershipPlanStepProps = {}) => {
  // Plan lives in the store so the reused sidebar/order summary can price it.
  const plan = useMembershipPlanStore((s) => s.plan);
  const setPlan = useMembershipPlanStore((s) => s.setPlan);

  // Real cadence prices come from the membership tiers (Stripe source of truth,
  // surfaced via the products query). useProducts is cached, so this is cheap.
  const language = useBookingFlowStore((s) => s.language);
  const tiers = useMembershipTiers().tiers;
  // react-i18next instance pinned to the booking language — used by <Trans> for
  // the savings footnote (embedded styled amounts).
  const { t: tt } = useTranslation(BOOKING_NS, { lng: languageMap[language] });

  // Largest "vs. booking separately" saving across cadences (shown in footnote).
  const maxSavingChf = useMemo(() => {
    const plans: RecommendedPlan[] = ["baseline", "twice", "quarterly"];
    return Math.max(
      ...plans.map(
        (p) => getSeparatePriceChf(p, tiers) - getCadencePriceChf(p, tiers),
      ),
    );
  }, [tiers]);

  // Card price + struck-through "vs. booking separately" comparison.
  const cardPrice = (p: RecommendedPlan) => getCadencePriceChf(p, tiers);
  const cardOriginal = (p: RecommendedPlan) => getSeparatePriceChf(p, tiers);

  // Seed the store on mount (skipped when reused in the "Change cadence" modal,
  // which must preserve the current selection).
  useEffect(() => {
    if (autoSeed) setPlan("baseline");
  }, [autoSeed, setPlan]);

  return (
    <div className="w-full max-w-[560px] mx-auto flex flex-col gap-8">
      {/* Title + subtext */}
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="font-normal text-2xl text-neutral-900 leading-tight">
          {translate(language, "membershipCadence.title")}
        </h1>
        <p className="font-normal text-sm text-neutral-600 max-w-[460px]">
          {translate(language, "membershipCadence.subtitle")}
        </p>
      </div>

      {/* Plans (no card backdrop — each tile is white with a soft shadow) */}
      <div className="flex flex-col gap-4 mt-2 lg:mt-4">
        {/* Plan options */}
        <div className="flex flex-col gap-4">
          <PlanCard
            language={language}
            selected={plan === "baseline"}
            onSelect={() => setPlan("baseline")}
            label={translate(language, "membershipCadence.planOnceLabel")}
            description={translate(language, "membershipCadence.planOnceDesc")}
            price={cardPrice("baseline")}
            followups={0}
            baseline
          />
          <PlanCard
            language={language}
            selected={plan === "twice"}
            onSelect={() => setPlan("twice")}
            label={translate(language, "membershipCadence.planTwiceLabel")}
            description={translate(language, "membershipCadence.planTwiceDesc")}
            price={cardPrice("twice")}
            originalPrice={cardOriginal("twice")}
            followups={1}
            recommended
          />
          <PlanCard
            language={language}
            selected={plan === "quarterly"}
            onSelect={() => setPlan("quarterly")}
            label={translate(language, "membershipCadence.planFourLabel")}
            description={translate(language, "membershipCadence.planFourDesc")}
            price={cardPrice("quarterly")}
            originalPrice={cardOriginal("quarterly")}
            followups={3}
          />
        </div>

        {/* Savings / follow-ups footnote — always shown. */}
        <p className="font-normal text-sm text-neutral-600 text-center px-4 mt-3 lg:mt-4">
          *{" "}
          <Trans
            t={tt}
            i18nKey="membershipCadence.savings"
            values={{
              max: maxSavingChf,
              full: FOLLOWUP_PRICE_CHF,
              member: FOLLOWUP_MEMBER_PRICE_CHF,
            }}
            components={[
              <span className="font-medium text-neutral-900" />,
              <span className="line-through" />,
            ]}
          />
        </p>
      </div>
    </div>
  );
};

export default MembershipPlanStep;
